import { API_URL } from '../config';

interface EnterpriseAdminData {
  email: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  clerkId: string;
}

/**
 * Checks if an admin user already exists in the system
 * @returns Promise with the result of the check
 */
export const checkAdminExists = async () => {
  try {
    const response = await fetch(`${API_URL}/enterprise/check-admin-exists`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to check admin existence');
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking admin existence:', error);
    throw error;
  }
};

/**
 * Registers a new enterprise admin account
 * @param adminData Admin and organization details
 * @returns Promise with the response data
 */
export const registerEnterpriseAdmin = async (adminData: EnterpriseAdminData) => {
  try {
    const response = await fetch(`${API_URL}/enterprise/admin/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(adminData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to register enterprise admin');
    }

    return await response.json();
  } catch (error) {
    console.error('Error registering enterprise admin:', error);
    throw error;
  }
};

/**
 * Validates an enterprise setup code
 * @param setupCode The setup code to validate
 * @returns Promise with validation result
 */
export const validateSetupCode = async (setupCode: string) => {
  try {
    const response = await fetch(`${API_URL}/enterprise/validate-setup-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ setupCode }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Invalid setup code');
    }

    return await response.json();
  } catch (error) {
    console.error('Error validating setup code:', error);
    throw error;
  }
};

/**
 * Completes admin registration after email verification
 * @param organizationData Organization setup information
 * @param clerkUserId User ID from Clerk after verification
 * @returns Promise with the response data
 */
export const completeEnterpriseSetup = async (
  organizationData: { 
    organizationName: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    organizationSize?: string;
    industry?: string;
  },
  clerkUserId: string
) => {
  try {
    const response = await fetch(`${API_URL}/enterprise/admin/complete-setup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...organizationData,
        clerkId: clerkUserId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to complete enterprise setup');
    }

    const result = await response.json();
    
    // Ensure there's a success flag in the response
    return {
      ...result,
      success: true
    };
  } catch (error) {
    console.error('Error completing enterprise setup:', error);
    throw error;
  }
};

/**
 * Get user's role from the API
 * @param userId The Clerk user ID
 * @param token Authentication token
 * @returns Promise with the user's role
 */
export const getUserRole = async (userId: string, token: string | null) => {
  if (!token) {
    console.warn('No authentication token provided for getUserRole');
    // Instead of throwing, return a default value or handle it differently
    return 'unknown';
  }
  
  try {
    console.log(`Requesting role for user ID: ${userId}`);
    
    const response = await fetch(`${API_URL}/users/role/${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-clerk-user-id': userId
      },
    });

    if (!response.ok) {
      console.error('Failed to get user role, status:', response.status);
      // Try to get the error details for better debugging
      try {
        const errorDetails = await response.json();
        console.error('Error details:', errorDetails);
      } catch (e) {
        // Ignore if we can't parse the error response
      }
      
      // For 404 errors, the user might not be in the database yet
      if (response.status === 404) {
        console.log('User not found in database - might need to create user first');
        return 'not_found';
      }
      
      throw new Error('Failed to get user role');
    }

    const data = await response.json();
    console.log('User role response:', data);
    
    // If role is returned directly
    if (data && typeof data.role === 'string') {
      return data.role.toLowerCase(); // Normalize to lowercase
    }
    
    // If the API returned something unexpected
    console.warn('Unexpected role data format:', data);
    return 'unknown';
  } catch (error) {
    console.error('Error getting user role:', error);
    throw error;
  }
};

/**
 * Get all users for admin management
 * @param token Authentication token
 * @returns Promise with array of users
 */
export const getUsers = async (token: string | null) => {
  if (!token) {
    console.warn('No authentication token provided for getUsers');
    // Instead of throwing immediately, return empty array
    return [];
  }

  // Get the user ID from the JWT token (assuming it's available in localStorage)
  const clerkUserId = localStorage.getItem('clerk-user-id') || '';
  console.log('Using clerk user ID from localStorage:', clerkUserId);

  try {
    // Try the API endpoint first
    console.log('Attempting to fetch users from', `${API_URL}/api/admin/users`);
    const response = await fetch(`${API_URL}/api/admin/users`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-clerk-user-id': clerkUserId
      },
    });

    if (!response.ok) {
      console.warn('Failed with /api prefix, trying without prefix');
      // If that fails, try the non-API prefix endpoint
      const fallbackResponse = await fetch(`${API_URL}/admin/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': clerkUserId
        },
      });
      
      if (!fallbackResponse.ok) {
        console.error('Failed to get users, status:', fallbackResponse.status);
        // Try to get the error details for better debugging
        try {
          const errorDetails = await fallbackResponse.json();
          console.error('Error details:', errorDetails);
        } catch (e) {
          // Ignore if we can't parse the error response
        }
        throw new Error('Failed to get users');
      }
      
      const data = await fallbackResponse.json();
      console.log('Successfully retrieved users from fallback endpoint');
      return data;
    }

    const data = await response.json();
    console.log('Successfully retrieved users from primary endpoint');
    return data;
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

/**
 * Deactivate or activate a user
 * @param userId User ID to update
 * @param deactivate Whether to deactivate (true) or activate (false)
 * @param token Authentication token
 * @returns Promise with updated user data
 */
export const deactivateUser = async (userId: string, deactivate: boolean, token: string | null) => {
  if (!token) {
    console.warn('No authentication token provided for deactivateUser');
    throw new Error('No authentication token provided');
  }

  // Get the requesting user's ID from localStorage
  const clerkUserId = localStorage.getItem('clerk-user-id') || '';

  try {
    // Try with /api prefix first
    const endpoint = `${API_URL}/api/admin/users/${userId}/status`;
    console.log(`Attempting to ${deactivate ? 'deactivate' : 'activate'} user via ${endpoint}`);
    
    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-clerk-user-id': clerkUserId
      },
      body: JSON.stringify({ active: !deactivate }),
    });

    if (!response.ok) {
      // Try fallback without /api prefix
      const fallbackEndpoint = `${API_URL}/admin/users/${userId}/status`;
      console.log(`Trying fallback endpoint: ${fallbackEndpoint}`);
      
      const fallbackResponse = await fetch(fallbackEndpoint, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': clerkUserId
        },
        body: JSON.stringify({ active: !deactivate }),
      });
      
      if (!fallbackResponse.ok) {
        console.error(`Failed to ${deactivate ? 'deactivate' : 'activate'} user, status:`, fallbackResponse.status);
        throw new Error(`Failed to ${deactivate ? 'deactivate' : 'activate'} user`);
      }
      
      const data = await fallbackResponse.json();
      console.log('User status updated successfully via fallback endpoint');
      return data;
    }

    const data = await response.json();
    console.log('User status updated successfully');
    return data;
  } catch (error) {
    console.error(`Error ${deactivate ? 'deactivating' : 'activating'} user:`, error);
    throw error;
  }
};

/**
 * Update user role (admin/user)
 * @param userId User ID to update
 * @param role New role for the user ('admin' or 'user')
 * @param token Authentication token
 * @returns Promise with updated user data
 */
export const updateUserRole = async (userId: string, role: string, token: string | null) => {
  if (!token) {
    console.warn('No authentication token provided for updateUserRole');
    throw new Error('No authentication token provided');
  }

  // Get the requesting user's ID from localStorage
  const clerkUserId = localStorage.getItem('clerk-user-id') || '';

  try {
    // Try with /api prefix first
    const endpoint = `${API_URL}/api/admin/users/${userId}/role`;
    console.log(`Attempting to update user role to ${role} via ${endpoint}`);
    
    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-clerk-user-id': clerkUserId
      },
      body: JSON.stringify({ role }),
    });

    if (!response.ok) {
      // Try fallback without /api prefix
      const fallbackEndpoint = `${API_URL}/admin/users/${userId}/role`;
      console.log(`Trying fallback endpoint: ${fallbackEndpoint}`);
      
      const fallbackResponse = await fetch(fallbackEndpoint, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': clerkUserId
        },
        body: JSON.stringify({ role }),
      });
      
      if (!fallbackResponse.ok) {
        console.error('Failed to update user role, status:', fallbackResponse.status);
        const errorData = await fallbackResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update user role');
      }
      
      const data = await fallbackResponse.json();
      console.log('User role updated successfully via fallback endpoint');
      return data;
    }

    const data = await response.json();
    console.log('User role updated successfully');
    return data;
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

/**
 * Delete a user
 * @param userId User ID to delete
 * @param token Authentication token
 * @returns Promise with result
 */
export const deleteUser = async (userId: string, token: string | null) => {
  if (!token) {
    console.warn('No authentication token provided for deleteUser');
    throw new Error('No authentication token provided');
  }

  // Get the requesting user's ID from localStorage
  const clerkUserId = localStorage.getItem('clerk-user-id') || '';

  try {
    // Try with /api prefix first
    const endpoint = `${API_URL}/api/admin/users/${userId}`;
    console.log(`Attempting to delete user via ${endpoint}`);
    
    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-clerk-user-id': clerkUserId
      },
    });

    if (!response.ok) {
      // Try fallback without /api prefix
      const fallbackEndpoint = `${API_URL}/admin/users/${userId}`;
      console.log(`Trying fallback endpoint: ${fallbackEndpoint}`);
      
      const fallbackResponse = await fetch(fallbackEndpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': clerkUserId
        },
      });
      
      if (!fallbackResponse.ok) {
        console.error('Failed to delete user, status:', fallbackResponse.status);
        throw new Error('Failed to delete user');
      }
      
      const data = await fallbackResponse.json();
      console.log('User deleted successfully via fallback endpoint');
      return data;
    }

    const data = await response.json();
    console.log('User deleted successfully');
    return data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Get all invitations
 * @param token Authentication token
 * @returns Promise with array of invitations
 */
export const getInvitations = async (token: string | null) => {
  if (!token) {
    throw new Error('No authentication token provided');
  }

  // Get the clerk user ID from localStorage
  const clerkUserId = localStorage.getItem('clerk-user-id') || '';

  try {
    const response = await fetch(`${API_URL}/admin/invitations`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-clerk-user-id': clerkUserId
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get invitations');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting invitations:', error);
    throw error;
  }
};

/**
 * Create a new invitation
 * @param email Email to invite
 * @param firstName First name of the person to invite
 * @param lastName Last name of the person to invite
 * @param token Authentication token
 * @param isExistingUser Optional flag indicating if the user already exists in Clerk
 * @returns Promise with the created invitation
 */
export const createInvitation = async (
  email: string, 
  firstName: string, 
  lastName: string, 
  token: string | null,
  isExistingUser?: boolean,
  role?: 'member' | 'admin'
) => {
  if (!token) {
    throw new Error('No authentication token provided');
  }

  // Get the clerk user ID from localStorage
  const clerkUserId = localStorage.getItem('clerk-user-id') || '';

  try {
    const response = await fetch(`${API_URL}/admin/invitations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-clerk-user-id': clerkUserId
      },
      body: JSON.stringify({ 
        email, 
        firstName, 
        lastName, 
        isExistingUser: isExistingUser || false,
        role: role || 'member'
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || errorData.message || 'Failed to create invitation');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating invitation:', error);
    throw error;
  }
};

/**
 * Delete an invitation
 * @param invitationId ID of invitation to delete
 * @param token Authentication token
 * @returns Promise with result
 */
export const deleteInvitation = async (invitationId: string, token: string | null) => {
  if (!token) {
    throw new Error('No authentication token provided');
  }

  // Get the clerk user ID from localStorage
  const clerkUserId = localStorage.getItem('clerk-user-id') || '';

  try {
    const response = await fetch(`${API_URL}/admin/invitations/${invitationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-clerk-user-id': clerkUserId
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete invitation');
    }

    // Check if there were any issues with revocation on backend
    if (data.warning) {
      console.warn('Invitation deleted but with warning:', data.warning);
      return { ...data, hasWarning: true };
    }

    return data;
  } catch (error) {
    console.error('Error deleting invitation:', error);
    throw error;
  }
};

/**
 * Resend an invitation
 * @param invitationId ID of invitation to resend
 * @param token Authentication token
 * @returns Promise with updated invitation
 */
export const resendInvitation = async (invitationId: string, token: string | null) => {
  if (!token) {
    throw new Error('No authentication token provided');
  }

  // Get the clerk user ID from localStorage
  const clerkUserId = localStorage.getItem('clerk-user-id') || '';

  try {
    const response = await fetch(`${API_URL}/admin/invitations/${invitationId}/resend`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-clerk-user-id': clerkUserId
      },
    });

    if (!response.ok) {
      throw new Error('Failed to resend invitation');
    }

    return await response.json();
  } catch (error) {
    console.error('Error resending invitation:', error);
    throw error;
  }
};

/**
 * Validate an invitation token
 * @param token Invitation token from URL
 * @returns Promise with validation result including email, firstName, and lastName if valid
 */
export const validateInvitation = async (token: string) => {
  try {
    const response = await fetch(`${API_URL}/invitations/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Invalid invitation');
    }

    return await response.json();
  } catch (error) {
    console.error('Error validating invitation:', error);
    throw error;
  }
};

/**
 * Accept an invitation
 * @param token The invitation token
 * @param userId The Clerk user ID of the registered user
 * @returns Promise with the acceptance result
 */
export const acceptInvitation = async (token: string, userId: string) => {
  try {
    const response = await fetch(`${API_URL}/invitations/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to accept invitation');
    }

    return await response.json();
  } catch (error) {
    console.error('Error accepting invitation:', error);
    throw error;
  }
};

/**
 * Register a user with an invitation token
 * @param token The invitation token
 * @param email The user's email address
 * @param firstName The user's first name
 * @param lastName The user's last name
 * @param password The user's password
 * @returns Promise with the registration result
 */
export const registerInvitedUser = async (
  token: string,
  email: string,
  firstName: string,
  lastName: string,
  password: string
): Promise<any> => {
  try {
    console.log(`Registering invited user with token: ${token}, email: ${email}`);
    
    const response = await fetch(`${API_URL}/invitations/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        token,
        email,
        firstName,
        lastName,
        password
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Registration error response:', errorData);
      throw new Error(errorData.details || errorData.error || 'Failed to register with invitation');
    }

    const result = await response.json();
    console.log('Registration successful:', result);
    return result;
  } catch (error) {
    console.error('Error in registerInvitedUser:', error);
    throw error;
  }
};

/**
 * Sets the active organization for a user
 * @param userId The Clerk user ID
 * @param organizationId The Clerk organization ID to set as active
 * @returns Promise with the response data
 */
export const setActiveOrganization = async (userId: string, organizationId: string) => {
  try {
    const response = await fetch(`${API_URL}/users/set-organization`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, organizationId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to set active organization');
    }

    return await response.json();
  } catch (error) {
    console.error('Error setting active organization:', error);
    throw error;
  }
};